import { useEffect } from "react";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { IAndITotals, infiltrationAndInflow } from "@/endpoints/dryp/infiltrationAndInflow";
import { IAndITimePeriodsValue } from "@/config/date";
import { StructureFilterParams } from "@/utils/filters";

const buildDistributionBreakdown = (totals: IAndITotals): Dryp.IAndISummaryParts => {
	const grandTotal = totals.wasteVolume + totals.infiltrationVolume + totals.inflowVolume;
	const unit = { id: 0, name: totals.unit.name, symbol: totals.unit.symbol };
	const percentUnit = { id: 0, name: "Percent", symbol: "%" };

	const part = (volume: number) => ({
		flow: { value: volume, unit },
		volume: { value: volume, unit },
		flowPerLength: null,
		ratio: { value: grandTotal > 0 ? (volume / grandTotal) * 100 : 0, unit: percentUnit },
	});

	return {
		waste: part(totals.wasteVolume),
		infiltration: part(totals.infiltrationVolume),
		inflow: part(totals.inflowVolume),
	};
};

export const useIAndITotals = (timePeriod: IAndITimePeriodsValue, filters?: StructureFilterParams) => {
	const totalsApi = useAsyncStateStore(infiltrationAndInflow.getTotals);
	const key = JSON.stringify({ timePeriod, filters });

	useEffect(() => {
		totalsApi.fetch(timePeriod, { isActive: true, ...filters });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);

	const totals = totalsApi.data;
	const days = totals?.days || 1;
	const infiltrationPerDay = totals ? totals.infiltrationVolume / days : 0;
	const inflowPerDay = totals ? totals.inflowVolume / days : 0;
	const sumIIPerDay = totals ? (totals.infiltrationVolume + totals.inflowVolume) / days : 0;
	const totalPerDay = totals ? totals.totalVolume / days : 0;
	const percentOfTotal = totalPerDay > 0 ? Math.abs((sumIIPerDay / totalPerDay) * 100).toFixed(0) : "0";
	const unitLabel = `${totals?.unit.symbol ?? "m³"}/day`;
	const missingConnectivityCount = totals?.structureIdsMissingConnectivity.length ?? 0;
	const distributionBreakdown = totals ? buildDistributionBreakdown(totals) : null;

	return {
		loading: totalsApi.loading,
		loaded: totalsApi.loaded,
		error: totalsApi.error,
		totals,
		infiltrationPerDay,
		inflowPerDay,
		sumIIPerDay,
		totalPerDay,
		percentOfTotal,
		unitLabel,
		missingConnectivityCount,
		distributionBreakdown,
	};
};

export const useIAndIWorstAffected = (
	timePeriod: IAndITimePeriodsValue,
	size: number,
	filters?: StructureFilterParams,
) => {
	const worstApi = useAsyncStateStore(infiltrationAndInflow.getSummaries);
	const key = JSON.stringify({ timePeriod, size, filters });

	useEffect(() => {
		worstApi.fetch(
			{ pageNumber: 0, pageSize: size },
			timePeriod,
			{ sort: "iAndIPerPipeLength", direction: "desc" },
			// Worst-affected ranks by measured I&I, so it is meaningless for structures with no data.
			{ isActive: true, ...filters, hasData: true },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key]);

	return {
		loading: worstApi.loading,
		loaded: worstApi.loaded,
		error: worstApi.error,
		items: worstApi.data?.items ?? [],
	};
};
